import { Fieldset } from '@headlessui/react';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { useEffect, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { ActorRefFrom } from 'xstate';
import {
  AsyncSelect,
  FormDialog,
  FormLayout,
  FormRow,
  FormSection,
  LoadingSpinner,
  Select,
} from '~/components';
import { TransferList } from '~/components/forms/TransferList';
import { US_STATES_AND_TERRITORIES } from '~/constants';
import { EmployerFacilityForm } from '~/features/market/facility';
import { EmployerFacilityValues } from '~/features/market/facility/types';
import { JOB_LISTING_STATE } from '~/features/xstateSystem';
import { useCustomForm } from '~/hooks/useCustomForm';
import { JobListing } from '~/types/hlx/jobListing';
import { jobListingMachine } from '../../job-listing/jobListing.machine';
import type { Context as JobListingContext } from '../../job-listing/types';
import { msaasListingsMachine } from '../../msaas-listing/msaasListing.machine';
import type { Context as MsaasListingContext } from '../../msaas-listing/types';
import { emptyFacilitiesForm } from './constants';
import type { Context as FacilitiesContext } from './types';
import {
  FacilitiesFormSchema,
  FacilitiesFormValues,
  FacilityOption,
  JobListingFacilitiesFormSchema,
  RemoteFacilitiesFormSchema,
} from './types';
import {
  facilityStateOptions,
  getEmployerFacilityId,
  loadEmployerFacilitiesOptions,
  toFacilityOption,
} from './utils';

const TAGS = CT.employer.listings.facilityForm;

type ListingActorRef =
  | ActorRefFrom<typeof msaasListingsMachine>
  | ActorRefFrom<typeof jobListingMachine>;

export const FacilityForm = ({
  footer,
  actor,
}: {
  footer: React.ReactNode;
  actor: ListingActorRef;
}) => {
  const [optionsLoading, setOptionsLoading] = useState(true);
  // [MCV-1536] A failed hydration must BLOCK this step, not fall through to an
  // empty form. `ON_LOAD` has already recorded the real listing id by then, and
  // the job-listing schema allows a states-only submission -- so continuing from
  // unhydrated RHF defaults PATCHes `primaryEmployerFacilityId: null` and PUTs
  // `facilities: []`, destroying the listing's persisted facility selections
  // after nothing worse than a transient GET failure.
  const [hydrationFailed, setHydrationFailed] = useState(false);
  const [hydrationRetry, setHydrationRetry] = useState(0);
  const [userTouchedStates, setUserTouchedStates] = useState(false);
  // [MCV-1536] Own the facilities options list in state instead of letting
  // AsyncSelect's `defaultOptions={true}` load it once on mount -- a facility
  // created via the nested "Add New Facility" dialog needs to be appended
  // here to ever show up in the dropdown (see the `employerFacility` effect
  // below). `optionsVersion` is handed to AsyncSelect as `cacheOptions`: react
  // -select's useAsync resets its typed-query cache whenever that value
  // changes identity, so a bump also busts any already-cached search results.
  const [facilityOptions, setFacilityOptions] = useState<FacilityOption[]>([]);
  const [optionsVersion, setOptionsVersion] = useState(0);
  // [MCV-1536] Two distinct facts, deliberately not collapsed into one ref:
  // which listing id currently OWNS the hydration (claimed before the fetch
  // starts, so a concurrent rerender can't race a second one), and which id has
  // actually FINISHED hydrating (so an adopted in-flight result can tell "no
  // one has filled the form yet" from "already filled -- leave the user's edits
  // alone").
  const hydratedListingIdRef = useRef<number | undefined>(undefined);
  const hydrationDoneIdRef = useRef<number | undefined>(undefined);
  // [MCV-1536] Identifies the attempt that currently owns the loading state.
  // The id refs above cannot serve this: the catch below RELEASES
  // `hydratedListingIdRef` on failure, so a check against it in `finally`
  // would no longer recognise the very attempt that just failed.
  const hydrationAttemptRef = useRef(0);
  const listing = useSelector(
    actor,
    (s) => (s.context as JobListingContext | MsaasListingContext).listing
  );
  const facilitiesStepRef = useSelector(
    actor,
    (s) => (s.context as JobListingContext | MsaasListingContext).facilitiesStepRef
  );
  const facilityRef = useSelector(
    facilitiesStepRef,
    (s) => (s.context as FacilitiesContext).facilityRef
  );
  const facility = useSelector(facilitiesStepRef, (s) => (s.context as FacilitiesContext).facility);
  const modal = useSelector(facilitiesStepRef, (s) => (s.context as FacilitiesContext).modal);
  const employerFacility = useSelector(
    facilitiesStepRef,
    (s) => (s.context as FacilitiesContext).employerFacility
  );
  const loading = useSelector(facilitiesStepRef, (s) => (s.context as FacilitiesContext).loading);

  // [MCV-1956] Only REMOTE: a Partial Remote (hybrid) listing still has a
  // place of work, so it keeps the facilities controls.
  const isRemote = listing?.workLocation === 'REMOTE';

  const methods = useCustomForm<FacilitiesFormValues>({
    // The remote schema carries no facilities, so its parse result is cast back
    // to the shared form type; onSubmitSuccess never reads facilities for it.
    resolver: zodResolver(
      isRemote
        ? (RemoteFacilitiesFormSchema as unknown as typeof FacilitiesFormSchema)
        : actor.id === JOB_LISTING_STATE
          ? JobListingFacilitiesFormSchema
          : FacilitiesFormSchema
    ),
    defaultValues: emptyFacilitiesForm,
  });

  const { getValues, reset, setValue, watch } = methods;

  useEffect(() => {
    const loadFacilitiesData = async () => {
      if (!listing) {
        setOptionsLoading(false);
        return;
      }
      if (hydratedListingIdRef.current === listing.id) {
        return;
      }
      setHydrationFailed(false);

      // [MCV-1536] Claim this listing id before the async work starts, not
      // after it resolves. A late-arriving `listing` reference for the SAME
      // id used to re-enter this effect while the first fetch was still in
      // flight; if the stale first call resolved later it would blindly
      // reset() the form from its own stale closure, discarding whatever the
      // user had already selected in between. Claiming eagerly makes any
      // concurrent re-entry return at the guard above instead of racing to a
      // second reset().
      //
      // The in-flight result is then ADOPTED rather than abandoned: this run
      // finishes hydrating even though its own effect run was cancelled (see
      // the ownership checks after the await). Cancellation here only means
      // `listing` got a new object reference -- the id is unchanged and the
      // replacement run returned at the guard above, so this fetch's result
      // is the only one coming. Releasing the claim and letting the
      // replacement refetch instead would re-enter setOptionsLoading(true),
      // which unmounts this whole subtree for a LoadingSpinner (see the
      // render below) -- tearing down an open dropdown mid-interaction and
      // remounting a form that then re-applies `employerFacility`.
      hydratedListingIdRef.current = listing.id;
      // Taken here rather than at the top of the effect: runs that return at
      // the guard above never touch the loading state, so they must not
      // invalidate the attempt that owns it.
      hydrationAttemptRef.current += 1;
      const attempt = hydrationAttemptRef.current;

      // [MCV-1536] Send ON_LOAD before the await (not after): the facilities
      // sub-machine then learns the real listingId even if the GET below
      // fails. It used to fire only after a successful fetch, so a failed
      // fetch left listingId stranded at its -1 default forever.
      facilitiesStepRef.send({
        type: 'ON_LOAD',
        payload: {
          listingId: listing.id,
        },
      });

      setOptionsLoading(true);
      try {
        const options = await loadEmployerFacilitiesOptions();

        // [MCV-1536] Ownership, not cancellation, decides whether this result
        // may be applied -- that is what makes an adopted in-flight fetch safe.
        // A DIFFERENT listing id took the claim while this fetch was running,
        // so this result is genuinely stale; applying it would reset() the form
        // to the wrong listing.
        if (hydratedListingIdRef.current !== listing.id) {
          return;
        }
        // This id is already hydrated, so a second reset() would clobber
        // whatever the user has selected since.
        if (hydrationDoneIdRef.current === listing.id) {
          return;
        }
        setFacilityOptions(options);

        const listingFacilityIds = (listing.facilities ?? []).map((facility) =>
          getEmployerFacilityId(facility)
        );
        const listingStateCodes = listing.stateCodes ?? [];
        const primaryEmployerFacilityId =
          actor.id === JOB_LISTING_STATE
            ? (listing as JobListing).primaryEmployerFacilityId
            : undefined;
        const existingFacilities = options.filter((option) =>
          listingFacilityIds.includes(Number(option.value))
        );
        const primaryFacility = existingFacilities.find(
          (facility) => Number(facility.value) === primaryEmployerFacilityId
        );

        reset({
          ...emptyFacilitiesForm,
          facilities: existingFacilities,
          primaryFacility: primaryFacility ?? emptyFacilitiesForm.primaryFacility,
          // [MCV-1546] Map over the persisted codes rather than filtering the
          // constant: a stored code the client list does not carry would
          // otherwise be dropped here and then deleted by the replace PUT.
          states: listingStateCodes.map(
            (code) =>
              US_STATES_AND_TERRITORIES.find((state) => state.value === code) ?? {
                value: code,
                label: code,
              }
          ),
        });
        hydrationDoneIdRef.current = listing.id;
        setUserTouchedStates(false);
      } catch (error) {
        console.error('[FacilityForm] Failed to load facility options', error);
        // [MCV-1536] This attempt never hydrated, so release the claim and let
        // a later `listing` update retry -- otherwise the guard above blocks
        // every future attempt on a claim nobody will ever fulfil. Only
        // release a claim still held for THIS id: a newer id may have taken
        // it while this fetch was failing.
        if (
          hydrationAttemptRef.current === attempt &&
          hydratedListingIdRef.current === listing.id &&
          hydrationDoneIdRef.current !== listing.id
        ) {
          hydratedListingIdRef.current = undefined;
          // Block the step rather than rendering unhydrated defaults the user
          // could submit over their saved facilities (see `hydrationFailed`).
          setHydrationFailed(true);
        }
      } finally {
        // [MCV-1536] Only the attempt that still owns the loading state may
        // clear it. Clearing unconditionally meant a FAILING attempt for one
        // listing switched the spinner off while a NEWER listing's hydration
        // was still in flight -- rendering the form on unhydrated defaults
        // that the user could submit over the new listing's saved facilities,
        // the same data loss the blocked state exists to prevent.
        //
        // Guarding on `cancelled` (the shape this had originally) is what
        // wedged the spinner forever: a run cancelled by a same-id rerender
        // skipped this, and the replacement returned at the guard above
        // without reaching it either. An attempt token has neither problem --
        // a same-id rerender never takes a new attempt, so this run still
        // owns the clear.
        if (hydrationAttemptRef.current === attempt) {
          setOptionsLoading(false);
        }
      }
    };

    loadFacilitiesData();
    // [MCV-1536] Deliberately no cleanup. Nothing here needs cancelling: an
    // in-flight fetch is adopted rather than abandoned (see the ownership
    // checks after the await), which is what keeps a same-id rerender from
    // either deadlocking on an unfulfillable claim or refetching -- and a
    // refetch would flip `optionsLoading` back on, unmounting this subtree
    // and any dropdown open inside it.
  }, [actor.id, facilitiesStepRef, listing, reset, hydrationRetry]);

  const onSubmitSuccess = (values: FacilitiesFormValues) => {
    // [MCV-1956] Remote: no facilities and no primary, only the chosen states.
    // Sending `facilities: []` is what drops the facilities a listing had
    // before it was switched to Remote (and jobListingSubmit then nulls the
    // primary), and states are always persisted because they are the only
    // location the listing has.
    if (isRemote) {
      facilitiesStepRef.send({
        type: 'ON_SUBMIT',
        payload: {
          facilities: [],
          states: values.states.map((state) => state.value),
          shouldPersistStates: true,
        },
      });
      return;
    }
    // [MCV-1546] The form hydrates `states` from listing.stateCodes before the
    // facilities PUT has derived anything, so a states PUT that omits the
    // selected facility's state deletes what the facilities PUT just wrote --
    // facility picked, states blank. Union them so the two calls agree, and
    // persist whenever a facility contributes a code so already-broken listings
    // repair themselves on their next save.
    const derived = facilityStateOptions(values.facilities).map((option) => option.value);
    const states = Array.from(new Set([...values.states.map((state) => state.value), ...derived]));
    const shouldPersistStates = userTouchedStates || derived.length > 0;
    const primaryFacility = values.primaryFacility?.value
      ? Number(values.primaryFacility.value)
      : undefined;
    facilitiesStepRef.send({
      type: 'ON_SUBMIT',
      payload: {
        facilities: values.facilities.map((facility) => Number(facility.value)),
        ...(primaryFacility !== undefined ? { primaryFacility } : {}),
        states,
        shouldPersistStates,
      },
    });
  };

  const onAddFacilitySubmitSuccess = (values: EmployerFacilityValues) => {
    facilitiesStepRef.send({ type: 'ON_NEW_FACILITY_SUBMIT', payload: { data: values } });
  };

  useEffect(() => {
    if (employerFacility) {
      const newOption = toFacilityOption(employerFacility);
      setValue('facilities', [...getValues('facilities'), newOption], {
        shouldDirty: true,
      });
      // [MCV-1536] AsyncSelect's `defaultOptions`/`cacheOptions` used to be
      // hardcoded `true`, so react-select's useAsync loaded the facilities
      // list once on mount and froze every typed query's results -- a
      // facility created here (via the nested "Add New Facility" dialog)
      // never appeared in the dropdown afterward, and with `hideSelectedOptions`
      // (isMulti) the just-added chip was invisible in the menu and
      // unrecoverable if backspaced back out. Appending it to the options
      // array we hand AsyncSelect syncs reactively; bumping the version busts
      // the typed-query cache too.
      setFacilityOptions((current) =>
        current.some((option) => option.value === newOption.value)
          ? current
          : [...current, newOption]
      );
      setOptionsVersion((version) => version + 1);
    }
  }, [employerFacility, getValues, setValue]);

  const selectedFacilities = watch('facilities');
  const primaryFacility = watch('primaryFacility');

  // [MCV-1546] Show the facility's state in the list as soon as the facility is
  // picked. Only ever adds, mirroring the API's add_only derivation, and never
  // marks the states list as user-touched.
  useEffect(() => {
    // [MCV-1956] A remote listing renders and submits no facilities, so a
    // hydrated one must not leak its state into the list.
    if (isRemote) return;
    const current = getValues('states');
    const missing = facilityStateOptions(selectedFacilities).filter(
      (option) => !current.some((state) => state.value === option.value)
    );
    if (missing.length === 0) return;
    setValue('states', [...current, ...missing], { shouldDirty: true });
  }, [isRemote, selectedFacilities, getValues, setValue]);

  // [MCV-1530] The primary-facility Select's options derive live from
  // `selectedFacilities`, but nothing else reconciles the CHOSEN primary when
  // a facility is removed -- Select renders whatever value RHF holds
  // regardless of whether it is still among `options`, so the UI kept showing
  // a primary that was no longer selectable. Left alone, submit still PATCHes
  // that stale primaryEmployerFacilityId, orphaning it once the facilities PUT
  // removes the association -- which is what made DELETE /employer-facilities
  // /{id} 409 "in use by one or more listings" forever afterward.
  useEffect(() => {
    if (
      primaryFacility?.value &&
      !selectedFacilities.some((facility) => facility.value === primaryFacility.value)
    ) {
      setValue('primaryFacility', emptyFacilitiesForm.primaryFacility, { shouldDirty: true });
    }
  }, [primaryFacility, selectedFacilities, setValue]);

  const onAddFacility = () => {
    facilitiesStepRef.send({ type: 'ON_ADD_NEW_FACILITY' });
  };

  const onSubmitError = (err: unknown) => {
    console.log('Facility Form Submit Error: ', err);
  };

  const handleFacilityClose = () => facilitiesStepRef.send({ type: 'ON_CLOSE_MODAL' });

  if (optionsLoading || loading)
    return (
      <div className="flex justify-center items-center min-h-[500px]">
        <LoadingSpinner />
      </div>
    );

  // [MCV-1536] Blocked, not merely annotated: rendering the form here would let
  // the user submit empty defaults over the listing's saved facilities.
  if (hydrationFailed)
    return (
      <div className="flex flex-col items-center justify-center gap-3 min-h-[500px] text-center">
        <p className="sub-heading-sb text-grey-100">Could not load facilities</p>
        <p className="body-text text-grey-500">
          Your saved facilities could not be loaded, so this step cannot be edited yet. Please try
          again.
        </p>
        <button
          type="button"
          className="body-text text-primary-50 underline"
          data-cy={TAGS.hydrationRetryBtn}
          onClick={() => setHydrationRetry((count) => count + 1)}
        >
          Retry
        </button>
      </div>
    );

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmitSuccess, onSubmitError)}>
          <FormLayout>
            <div className="min-h-[400px]">
              <FormSection componentsPerLine={1}>
                {/* [MCV-1529] The facilities select and its "add a facility"
                  action render as ONE FormRow (not two) so they read as a
                  single visual unit -- QA repeatedly flagged the button as
                  floating far below the select box ("Button still very far
                  away" after earlier attempts). AsyncSelect now collapses
                  absent feedback by default, and keeping the related action in
                  the same row avoids a second layout gap. `mt-3`, combined
                  with FormRow's gap-2, leaves a deliberate 20px separation,
                  verified here via FacilityFormLayout.browser.spec.tsx. */}
                {!isRemote && (
                  <FormRow>
                    <AsyncSelect
                      name={'facilities'}
                      cyTag={TAGS.facilitiesSelect}
                      label={'Facilities Where Services Will Be Provided'}
                      isMulti
                      minChars={false}
                      // [MCV-1294][MCV-1536] The wizard runs inside ModalDialog's
                      // nested overflow-y-auto clip boxes; without a portal the menu
                      // renders `absolute z-[1]` inside them and clips when the
                      // modal is short.
                      loadOptions={loadEmployerFacilitiesOptions}
                      // [MCV-1536] See the `facilityOptions`/`optionsVersion` state
                      // above -- keeps a newly created facility selectable without
                      // a remount.
                      defaultOptions={facilityOptions}
                      cacheOptions={optionsVersion}
                      // [MCV-1575] Typing here filters the employer's own list; it
                      // does not search the wider facility directory. A bare "No
                      // Results" left users who typed a facility they had not added
                      // yet with no next step, even though the add-facility link is
                      // directly below.
                      noOptionsMessage="No matching facility on your list — use “If your facility is not listed, add it here” below"
                    />
                    <Fieldset className="mt-3 flex pr-1">
                      <button
                        className="flex items-center gap-2 hover:cursor-pointer"
                        data-cy={TAGS.addBtn}
                        type="button"
                        onClick={onAddFacility}
                      >
                        <PlusCircleIcon aria-hidden="true" className="size-6 text-primary-50" />
                        <p className="body-text-sb">If your facility is not listed, add it here</p>
                      </button>
                    </Fieldset>
                  </FormRow>
                )}
                {!isRemote && actor.id === 'jobListingState' && (
                  <FormRow>
                    <Select
                      name={'primaryFacility'}
                      label={'Primary Facility'}
                      description="Select which of the selected facilities is the primary facility.
                     This is where the majority of work will be performed or will be performed for."
                      cyTag={TAGS.primaryFacilitySelect}
                      options={selectedFacilities}
                      // [MCV-1294][MCV-1536] Same clipping failure mode as the
                      // facilities AsyncSelect above -- this menu lives inside
                      // ModalDialog's nested overflow-y-auto clip boxes too.
                    />
                  </FormRow>
                )}
                <FormRow>
                  <TransferList
                    name={'states'}
                    label={'States Where Services Will Be Provided'}
                    description={
                      isRemote
                        ? 'Select the states and territories where providers must be licensed to work remotely.'
                        : 'Select the states and territories where services will be provided.'
                    }
                    availableOptions={US_STATES_AND_TERRITORIES}
                    availableLabel={'Available States & Territories'}
                    selectedLabel={'Selected'}
                    cyTag={TAGS.statesSelect}
                    filterable
                    onChange={() => setUserTouchedStates(true)}
                  />
                </FormRow>
              </FormSection>
            </div>
            {footer}
          </FormLayout>
        </form>
      </FormProvider>
      <FormDialog
        title={'Add New Employer Facility'}
        editing={false}
        creating={modal}
        onClose={handleFacilityClose}
        zIndex="nested"
      >
        <EmployerFacilityForm
          facilityRef={facilityRef}
          facility={facility}
          onSubmitSuccess={onAddFacilitySubmitSuccess}
          facilityDialogZIndex="top"
        />
      </FormDialog>
    </>
  );
};
